/**
 * iter75 — Painel de Permissões clicável (Dialog abrível a partir de UsersPermissions).
 *
 * Fluxo:
 *   1. Master seleciona um usuário na lista → clica em "Permissões".
 *   2. Dialog carrega:
 *        - GET /api/permissions/catalog         (capabilities + perfis)
 *        - GET /api/users/{user_id}/effective-permissions
 *   3. Toggle altera apenas o estado local (dirty).
 *   4. "Salvar" → PUT /api/users/{user_id}/permissions
 *   5. "Restaurar padrão do perfil" → limpa overrides localmente.
 *   6. Fechar/cancelar/trocar perfil com dirty pede confirmação.
 *
 * Estados por permissão:
 *   - Padrão do perfil (herdada, sem override)
 *   - Liberada manualmente (override = true)
 *   - Bloqueada manualmente (override = false)
 */
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, Info, RotateCcw, CircleCheck, CircleX, Save } from "lucide-react";

// Categorias por prefixo. NÃO renderizamos aliases legados de iter74
// (clients.read, agenda.write, central.*, etc.) — o backend segue aceitando,
// mas o painel mostra apenas as capabilities canônicas.
const CATEGORY_ORDER = [
  "Usuários", "Clientes", "Agenda", "Caixa", "Financeiro",
  "Mensagens", "Segmentos", "Automações", "Relatórios", "Configurações",
];

const CATEGORY_PREFIXES = {
  "Usuários": ["users."],
  "Clientes": ["clients."],
  "Agenda": ["agenda."],
  "Caixa": ["cash."],
  "Financeiro": ["finance."],
  "Mensagens": ["social."],
  "Segmentos": ["segments."],
  "Automações": ["automations."],
  "Relatórios": ["reports."],
  "Configurações": ["settings.", "integrations."],
};

// Rótulos amigáveis para cada capability. Fallback usa o próprio id.
const CAP_LABELS = {
  "users.view": "Ver lista de usuários",
  "users.create": "Criar novos usuários",
  "users.edit": "Editar dados de usuários",
  "users.disable": "Desativar usuários",
  "users.reset_password": "Redefinir senhas",
  "users.permissions": "Gerenciar permissões",
  "clients.view": "Ver clientes",
  "clients.create": "Cadastrar clientes",
  "clients.edit": "Editar clientes",
  "clients.delete": "Excluir clientes",
  "clients.export": "Exportar base de clientes",
  "agenda.view_all": "Ver agenda de toda a equipe",
  "agenda.view_own": "Ver apenas a própria agenda",
  "agenda.create": "Criar agendamentos",
  "agenda.edit": "Editar agendamentos",
  "agenda.cancel": "Cancelar agendamentos",
  "agenda.confirm": "Confirmar agendamentos",
  "cash.view": "Ver caixa",
  "cash.open": "Abrir caixa",
  "cash.close": "Fechar caixa",
  "cash.receive": "Receber pagamentos",
  "cash.refund": "Estornar/refund",
  "cash.edit_closed": "Editar caixa já fechado",
  "finance.view": "Ver financeiro",
  "finance.view_reports": "Ver relatórios financeiros",
  "finance.export": "Exportar dados financeiros",
  "finance.edit": "Editar lançamentos financeiros",
  "social.view": "Ver Central de Mensagens",
  "social.reply": "Responder mensagens",
  "social.mark_read": "Marcar como lida",
  "segments.view": "Ver segmentos",
  "segments.create": "Criar/editar segmentos",
  "segments.send": "Disparar segmentos",
  "automations.view": "Ver automações",
  "automations.create": "Criar/editar automações",
  "automations.execute": "Executar automações",
  "settings.view": "Ver Configurações",
  "settings.edit": "Editar Configurações",
  "integrations.manage": "Gerenciar integrações (Z-API, Google, etc.)",
  "reports.view": "Ver relatórios",
  "reports.export": "Exportar relatórios",
};

function labelOf(cap) {
  return CAP_LABELS[cap] || cap;
}

function categorize(capabilities) {
  const groups = Object.fromEntries(CATEGORY_ORDER.map((c) => [c, []]));
  const others = [];
  for (const cap of capabilities) {
    let placed = false;
    for (const [cat, prefixes] of Object.entries(CATEGORY_PREFIXES)) {
      if (prefixes.some((p) => cap.startsWith(p))) {
        groups[cat].push(cap);
        placed = true;
        break;
      }
    }
    if (!placed) others.push(cap);
  }
  return { groups, others };
}

// Estado da UI:
//   - override === undefined  → herdada do perfil (source depende de in_profile)
//   - override === true       → liberada manualmente
//   - override === false      → bloqueada manualmente
// Ao clicar em um toggle:
//   - Se estava "herdada" e usuário desliga → override=false (se in_profile) ou permanece false (se não in_profile e clicou pra desligar? nunca chega aqui)
//   - Se estava "herdada" e usuário liga → override=true
//   - Se estava override=true e usuário desliga → volta para herdada se in_profile, ou override=false se quiser? mantemos simples: volta para herdada
//   - Se estava override=false e usuário liga → volta para herdada se in_profile true, ou override=true se in_profile false
// Regra simples: um clique sempre alterna o "granted" resultante,
// preferindo REMOVER o override quando o novo estado coincide com in_profile.
function nextOverrideState(currentOverride, inProfile, wantGranted) {
  if (wantGranted === inProfile) {
    // Estado desejado bate com perfil → remove override (volta pra herdada)
    return undefined;
  }
  // Estado desejado difere do perfil → precisa de override explícito
  return wantGranted;
}

export function PermissionsPanel({ open, onOpenChange, targetUser, catalog, onSaved, onDirtyChange, users, onSwitchUser }) {
  const { apiCall, user: currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [effective, setEffective] = useState(null);   // vindo do GET
  const [localProfile, setLocalProfile] = useState(null);
  const [localOverrides, setLocalOverrides] = useState({});   // { cap: bool | undefined }
  const [confirmClose, setConfirmClose] = useState(false);

  const isSelfMaster = targetUser?.role === "master";
  const isOtherMaster = targetUser?.role === "master" && targetUser?.user_id !== currentUser?.user_id;
  const readOnly = isOtherMaster;

  const load = async () => {
    if (!targetUser) return;
    setLoading(true);
    try {
      const r = await apiCall("get", `/users/${targetUser.user_id}/effective-permissions`);
      setEffective(r.data);
      setLocalProfile(r.data.profile || null);
      setLocalOverrides({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar permissões");
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && targetUser) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, targetUser?.user_id]);

  const profileFromEffective = effective?.profile || null;
  const profileDirty = localProfile !== profileFromEffective;
  const overridesDirty = Object.keys(localOverrides).length > 0;
  const isDirty = profileDirty || overridesDirty;

  useEffect(() => {
    onDirtyChange?.(isDirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty]);

  // Constrói matriz efetiva pós-edições locais.
  // Como o backend nos dá as caps com in_profile, aplicamos os overrides locais em cima.
  const capsToShow = catalog?.capabilities || [];
  const grouped = useMemo(() => categorize(capsToShow), [capsToShow]);

  // Mapa in_profile pra localProfile (se profile mudou, recalcula pelos defaults do catalog)
  const profileDefaults = useMemo(() => {
    if (!catalog || !localProfile) return new Set();
    return new Set(catalog.profiles?.[localProfile] || []);
  }, [catalog, localProfile]);

  const stateFor = (cap) => {
    // Se profile mudou localmente, o "in_profile" precisa refletir o novo profile.
    // Caso contrário, usa o que veio do backend.
    const in_profile = profileDirty
      ? profileDefaults.has(cap)
      : !!effective?.permissions?.[cap]?.in_profile;

    if (cap in localOverrides) {
      const val = localOverrides[cap];
      if (val === undefined) {
        return { granted: in_profile, source: in_profile ? "profile" : "denied", in_profile };
      }
      return {
        granted: !!val,
        source: val ? "override_true" : "override_false",
        in_profile,
      };
    }
    // Se profile mudou, ignora overrides antigos do backend (perfil novo redefine defaults)
    // Se não mudou, usa dados do backend
    if (profileDirty) {
      return { granted: in_profile, source: in_profile ? "profile" : "denied", in_profile };
    }
    const eff = effective?.permissions?.[cap];
    return eff || { granted: false, source: "denied", in_profile: false };
  };

  const handleToggle = (cap) => {
    if (readOnly) return;
    const cur = stateFor(cap);
    const wantGranted = !cur.granted;
    const next = nextOverrideState(undefined, cur.in_profile, wantGranted);
    setLocalOverrides((prev) => {
      const copy = { ...prev };
      if (next === undefined) {
        delete copy[cap];    // volta para herdada
      } else {
        copy[cap] = next;
      }
      return copy;
    });
  };

  const handleProfileChange = (newProfile) => {
    if (readOnly) return;
    // Trocar profile também descarta overrides locais (reset limpo)
    setLocalProfile(newProfile);
    setLocalOverrides({});
  };

  const handleRestoreDefaults = () => {
    if (readOnly) return;
    setLocalOverrides({});
    setLocalProfile(effective?.profile || null);
    toast.message("Alterações revertidas para o padrão do perfil (não salvo ainda)");
  };

  const handleSave = async () => {
    if (!targetUser || readOnly) return;
    setSaving(true);
    try {
      // Monta overrides finais: pega o estado atual (com in_profile do NOVO profile se mudou),
      // se profile mudou nós enviamos overrides={} (limpar) mais os que o usuário forçou manualmente.
      // Se profile NÃO mudou: enviamos o mapa completo de overrides existentes MENOS os "undefined",
      // MAIS os locais.
      let finalOverrides = {};
      if (profileDirty) {
        // Reset completo: só overrides locais explícitos
        Object.entries(localOverrides).forEach(([cap, val]) => {
          if (val !== undefined) finalOverrides[cap] = val;
        });
      } else {
        // Mantém overrides antigos + aplica locais
        Object.entries(effective?.permissions || {}).forEach(([cap, info]) => {
          if (info.source === "override_true") finalOverrides[cap] = true;
          if (info.source === "override_false") finalOverrides[cap] = false;
        });
        Object.entries(localOverrides).forEach(([cap, val]) => {
          if (val === undefined) delete finalOverrides[cap];
          else finalOverrides[cap] = val;
        });
      }

      const payload = {
        permission_overrides: finalOverrides,
      };
      if (profileDirty) payload.profile = localProfile;

      const r = await apiCall("put", `/users/${targetUser.user_id}/permissions`, payload);
      setEffective(r.data);
      setLocalProfile(r.data.profile || null);
      setLocalOverrides({});
      toast.success("Permissões salvas");
      onSaved?.();
    } catch (e) {
      // Não perde alterações locais em erro
      toast.error(e?.response?.data?.detail || "Falha ao salvar — suas alterações locais foram mantidas");
    } finally {
      setSaving(false);
    }
  };

  const tryClose = () => {
    if (isDirty) {
      setConfirmClose(true);
    } else {
      onOpenChange(false);
    }
  };

  const forceClose = () => {
    setConfirmClose(false);
    setLocalOverrides({});
    setLocalProfile(effective?.profile || null);
    onOpenChange(false);
  };

  // Troca de usuário dentro do painel (com confirmação se dirty)
  const [pendingSwitchId, setPendingSwitchId] = useState(null);
  const requestSwitch = (userId) => {
    if (!userId || userId === targetUser?.user_id) return;
    if (isDirty) {
      setPendingSwitchId(userId);
    } else {
      onSwitchUser?.(userId);
    }
  };
  const confirmInternalSwitch = () => {
    const id = pendingSwitchId;
    setPendingSwitchId(null);
    setLocalOverrides({});
    setLocalProfile(effective?.profile || null);
    onSwitchUser?.(id);
  };

  if (!targetUser) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) tryClose(); else onOpenChange(true); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="permissions-panel-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck size={20} /> Permissões — {targetUser.name}
            </DialogTitle>
            <DialogDescription>
              Configure o perfil padrão e ajuste permissões individuais. As alterações só são aplicadas ao clicar em Salvar.
            </DialogDescription>
          </DialogHeader>

          {/* Seletor rápido de usuário (evita fechar painel para trocar) */}
          {users && users.length > 1 && (
            <div className="flex items-center gap-3 border rounded p-2 bg-slate-50">
              <div className="text-xs text-muted-foreground flex-1">
                Trocar usuário sem fechar o painel:
              </div>
              <Select
                value={targetUser?.user_id || ""}
                onValueChange={requestSwitch}
              >
                <SelectTrigger className="w-64 h-8 text-xs" data-testid="permissions-panel-user-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.user_id} value={u.user_id} data-testid={`switch-user-option-${u.user_id}`}>
                      {u.name} — {u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Cabeçalho com dados do usuário */}
          <div className="grid grid-cols-2 gap-3 text-sm bg-slate-50 border rounded p-3" data-testid="permissions-panel-userinfo">
            <div><span className="text-muted-foreground">Nome:</span> <b>{targetUser.name}</b></div>
            <div><span className="text-muted-foreground">E-mail:</span> {targetUser.email}</div>
            <div>
              <span className="text-muted-foreground">Perfil atual:</span>{" "}
              <Badge variant="outline" data-testid="permissions-panel-current-profile">
                {effective?.profile || "— (fallback)"}
              </Badge>
            </div>
            <div>
              <span className="text-muted-foreground">Unidade(s):</span>{" "}
              {(effective?.unit_ids || []).length ? (effective.unit_ids.join(", ")) : "todas"}
            </div>
            <div>
              <span className="text-muted-foreground">Status:</span>{" "}
              <Badge className={effective?.active === false ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}>
                {effective?.active === false ? "Inativo" : "Ativo"}
              </Badge>
            </div>
            <div>
              <span className="text-muted-foreground">Papel:</span> {targetUser.role}
            </div>
          </div>

          {/* Trocar perfil */}
          <div className="flex items-center gap-3 border rounded p-3">
            <div className="flex-1">
              <div className="text-sm font-semibold">Perfil padrão</div>
              <div className="text-xs text-muted-foreground">Define os defaults. Overrides individuais ainda podem ser aplicados abaixo.</div>
            </div>
            <Select
              value={localProfile || ""}
              onValueChange={handleProfileChange}
              disabled={readOnly}
            >
              <SelectTrigger className="w-56" data-testid="permissions-panel-profile-select">
                <SelectValue placeholder="— sem perfil —" />
              </SelectTrigger>
              <SelectContent>
                {(catalog?.profile_names || []).filter((p) => p !== "master").map((p) => (
                  <SelectItem key={p} value={p} data-testid={`profile-option-${p}`}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isDirty && (
            <div className="text-xs bg-amber-50 border border-amber-200 rounded px-3 py-2 flex items-center gap-2" data-testid="permissions-panel-dirty-banner">
              <Info size={14} className="text-amber-700" />
              <span>Você tem alterações não salvas.</span>
            </div>
          )}

          {readOnly && (
            <div className="text-xs bg-red-50 border border-red-200 rounded px-3 py-2 flex items-center gap-2">
              <Info size={14} className="text-red-700" />
              <span>Não é permitido alterar permissões de outro master.</span>
            </div>
          )}

          <Separator />

          {/* Legenda */}
          <div className="text-xs flex flex-wrap gap-3" data-testid="permissions-panel-legend">
            <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-slate-400" /> Padrão do perfil</span>
            <span className="inline-flex items-center gap-1"><CircleCheck size={12} className="text-emerald-600" /> Liberada manualmente</span>
            <span className="inline-flex items-center gap-1"><CircleX size={12} className="text-red-600" /> Bloqueada manualmente</span>
          </div>

          {loading ? (
            <div className="p-6 text-center text-muted-foreground text-sm">Carregando permissões…</div>
          ) : (
            <div className="space-y-4">
              {CATEGORY_ORDER.map((cat) => {
                const caps = grouped.groups[cat] || [];
                if (!caps.length) return null;
                return (
                  <div key={cat} className="border rounded" data-testid={`permissions-category-${cat}`}>
                    <div className="px-3 py-2 border-b bg-slate-50 text-sm font-semibold">{cat}</div>
                    <div className="divide-y">
                      {caps.map((cap) => {
                        const st = stateFor(cap);
                        const src = st.source;
                        return (
                          <div key={cap} className="flex items-center gap-3 px-3 py-2" data-testid={`permission-row-${cap}`}>
                            <div className="flex-1">
                              <div className="text-sm">{labelOf(cap)}</div>
                              <div className="text-[11px] text-muted-foreground">{cap}</div>
                            </div>
                            <div className="w-40 text-right">
                              {src === "profile" && (
                                <span className="inline-flex items-center gap-1 text-[11px] text-slate-600" data-testid={`permission-badge-${cap}`}>
                                  <span className="inline-block h-2 w-2 rounded-full bg-slate-400" />
                                  Padrão do perfil
                                </span>
                              )}
                              {src === "denied" && (
                                <span className="inline-flex items-center gap-1 text-[11px] text-slate-500" data-testid={`permission-badge-${cap}`}>
                                  <span className="inline-block h-2 w-2 rounded-full bg-slate-300" />
                                  Não incluída no perfil
                                </span>
                              )}
                              {src === "override_true" && (
                                <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700" data-testid={`permission-badge-${cap}`}>
                                  <CircleCheck size={12} /> Liberada manualmente
                                </span>
                              )}
                              {src === "override_false" && (
                                <span className="inline-flex items-center gap-1 text-[11px] text-red-700" data-testid={`permission-badge-${cap}`}>
                                  <CircleX size={12} /> Bloqueada manualmente
                                </span>
                              )}
                            </div>
                            <Switch
                              checked={st.granted}
                              onCheckedChange={() => handleToggle(cap)}
                              disabled={readOnly}
                              data-testid={`permission-toggle-${cap}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              {grouped.others.length > 0 && (
                <div className="border rounded">
                  <div className="px-3 py-2 border-b bg-slate-50 text-sm font-semibold">Outros</div>
                  <div className="divide-y">
                    {grouped.others.map((cap) => {
                      const st = stateFor(cap);
                      return (
                        <div key={cap} className="flex items-center gap-3 px-3 py-2" data-testid={`permission-row-${cap}`}>
                          <div className="flex-1">
                            <div className="text-sm">{labelOf(cap)}</div>
                            <div className="text-[11px] text-muted-foreground">{cap}</div>
                          </div>
                          <Switch
                            checked={st.granted}
                            onCheckedChange={() => handleToggle(cap)}
                            disabled={readOnly}
                            data-testid={`permission-toggle-${cap}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:justify-between">
            <Button
              variant="outline"
              onClick={handleRestoreDefaults}
              disabled={readOnly || (!overridesDirty && !profileDirty)}
              data-testid="permissions-restore-defaults-btn"
            >
              <RotateCcw size={14} className="mr-1" />
              Restaurar padrão do perfil
            </Button>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={tryClose} data-testid="permissions-cancel-btn">Cancelar</Button>
              <Button
                onClick={handleSave}
                disabled={saving || readOnly || !isDirty}
                data-testid="permissions-save-btn"
              >
                <Save size={14} className="mr-1" />
                {saving ? "Salvando…" : "Salvar alterações"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
        <AlertDialogContent data-testid="permissions-confirm-close-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Descartar alterações?</AlertDialogTitle>
            <AlertDialogDescription>
              Você tem alterações não salvas. Se sair agora elas serão perdidas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="permissions-confirm-close-keep">Manter editando</AlertDialogCancel>
            <AlertDialogAction onClick={forceClose} data-testid="permissions-confirm-close-discard">
              Descartar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingSwitchId} onOpenChange={(o) => !o && setPendingSwitchId(null)}>
        <AlertDialogContent data-testid="permissions-switch-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Descartar alterações?</AlertDialogTitle>
            <AlertDialogDescription>
              Há alterações não salvas em <b>{targetUser?.name}</b>. Trocar de usuário vai descartá-las.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="permissions-switch-cancel">Manter editando</AlertDialogCancel>
            <AlertDialogAction onClick={confirmInternalSwitch} data-testid="permissions-switch-confirm">
              Descartar e trocar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
